import { FC, useRef, useState } from 'react';

import accountCreateEmployeeSearchFormFieldTouch from '@hh.ru/analytics-js-events/build/clickme/employer/accounts_control/account_create/account_create_employee_search_form_field_touch';
import accountEditEmployeeSearchFormFieldTouch from '@hh.ru/analytics-js-events/build/clickme/employer/accounts_control/account_edit/account_edit_employee_search_form_field_touch';
import { DataProvider, Input, StaticDataFetcherItem, Suggest, VSpacing } from '@hh.ru/magritte-ui';
import { BarsOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { UsersTagList } from 'components/AccountCreate/components/UsersTagList';
import { useCompositeSelectionContext } from 'components/common/CompositeSelection/contexts';

import { useSelector } from 'store/utils';

interface AccountUsersInputProps {
    provider: DataProvider<StaticDataFetcherItem>;
    accountId: number | null | undefined;
}

const TrlKeys = {
    title: 'account.create.modal.users.title',
    notFound: 'account.create.modal.users.notFound',
    reset: 'account.create.modal.users.cancel',
};

export const AccountUsersInput: FC<AccountUsersInputProps> = ({ provider, accountId }) => {
    const suggestRef = useRef<HTMLDivElement>(null);
    const { setTreeSelectorModalVisibility, collection, value, onChange } = useCompositeSelectionContext();
    const accountUsers = useSelector((state) => state.accounts.accountUsers);

    const trl = useTrls();
    const [inputValue, setInputValue] = useState('');

    const handleChange = (selectedValue: string, isValueFromList?: boolean) => {
        if (isValueFromList) {
            suggestRef.current?.blur();
            return;
        }
        setInputValue(selectedValue);
    };

    const handleSelect = (selectedValue: string) => {
        if (!value.includes(selectedValue)) {
            const foundItem = collection.toList().find((item) => item.id === selectedValue);
            if (foundItem) {
                onChange((prev) => ({ ...prev, value: [...value, foundItem.id] }));
                setInputValue('');
                return true;
            }
        }
        return false;
    };

    const handleFocus = () => {
        if (accountId) {
            accountEditEmployeeSearchFormFieldTouch();
        } else {
            accountCreateEmployeeSearchFormFieldTouch();
        }
    };

    return (
        <>
            <div onFocus={handleFocus}>
                <Suggest
                    ref={suggestRef}
                    dataProvider={provider}
                    inputValue={inputValue}
                    headerTitle={trl(TrlKeys.title)}
                    errorPlaceholder={trl(TrlKeys.notFound)}
                    trls={{
                        resetButton: trl(TrlKeys.reset),
                    }}
                    onSelectValidator={handleSelect}
                    onChange={handleChange}
                    input={{
                        component: Input,
                        props: {
                            size: 'medium',
                            placeholder: trl('account.create.users.placeholder'),
                            buttonIcon: BarsOutlinedSize24,
                            onButtonClick: () => setTreeSelectorModalVisibility(true),
                            'data-qa': 'employee-name',
                        },
                    }}
                />
            </div>
            {accountUsers.length > 0 && <VSpacing default={8} />}
            <UsersTagList isShort openAllUsers={() => setTreeSelectorModalVisibility(true)} />
        </>
    );
};
